import {Button, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import AlarmModuleItemsPurgeEventsButton from './AlarmModuleItemsPurgeEventsButton';
import AlarmModuleItemsPurgeNodeButton from './AlarmModuleItemsPurgeNodeButton';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  alarmModuleIds: number[];
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onDone?: () => void;
}

const AlarmModulesPurgeButton = <T extends ComponentType = typeof Button>({
  alarmModuleIds,
  component,
  componentProps,
  children,
  onDone,
}: Props<T>) => {
  const Component = component ?? Button;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
  const menuOpen = !!menuAnchorEl;

  const count = useMemo(() => {
    return alarmModuleIds?.length ?? 0;
  }, [alarmModuleIds]);

  const string = useMemo(() => {
    return `${count} Alarm module${count > 1 ? 's' : ''}`;
  }, [count]);

  const purgeAllTitle = useMemo(() => {
    return `Device (${string})`;
  }, [count]);

  const purgeTransactionTitle = useMemo(() => {
    return `Transactions (${string})`;
  }, [count]);

  const purgeAllButtonTitle = useMemo(() => {
    return `Purge Device (${string})`;
  }, [count]);

  const purgeTransactionButtonTitle = useMemo(() => {
    return `Purge Transactions (${string})`;
  }, [count]);

  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onBackdropClick={() => setMenuAnchorEl(undefined)}
      >
        <AlarmModuleItemsPurgeNodeButton
          alarmModuleIds={alarmModuleIds}
          buttonTitle={purgeAllButtonTitle}
          component={MenuItem}
          onDone={() => {
            setMenuAnchorEl(undefined);
            onDone?.();
          }}
        >
          {purgeAllTitle}
        </AlarmModuleItemsPurgeNodeButton>

        <AlarmModuleItemsPurgeEventsButton
          alarmModuleIds={alarmModuleIds}
          buttonTitle={purgeTransactionButtonTitle}
          component={MenuItem}
          onDone={() => {
            setMenuAnchorEl(undefined);
            onDone?.();
          }}
        >
          {purgeTransactionTitle}
        </AlarmModuleItemsPurgeEventsButton>
      </Menu>
    </>
  );
};

export default AlarmModulesPurgeButton;
